import type { TFunction } from 'i18next';
import type {
  GetPortfolioKpisResponseDto,
  PortfolioAttentionDto,
  PortfolioComplianceDto,
  PortfolioPenaltiesDto,
} from '~/app/services/operator-portfolio/portfolio-kpi.types';
import type { PenaltyBreakdownLine } from '~/app/components/dashboard/PenaltyBreakdownCard/PenaltyBreakdownCard';
import type {
  OperatorPortfolioPropertiesDto,
  PortfolioContractStatus,
  PortfolioOwnership,
} from '~/app/services/operator-portfolio/operator-portfolio.types';
import type {
  PortfolioPenaltyCardViewModel,
  PortfolioPropertyRowViewModel,
  PortfolioStatCardViewModel,
} from './PortfolioPage.types';

export const PORTFOLIO_PAGE_SIZE = 6;

export function formatPenaltyEuros(amount: number, locale: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function progressPercent(current: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((current / total) * 100));
}

export function mapAttentionStat(
  attention: PortfolioAttentionDto,
  t: TFunction<'dashboard'>,
): PortfolioStatCardViewModel {
  const { needingAttentionCount, totalChargers, breachOrLateCount } = attention;
  const metric =
    needingAttentionCount != null && totalChargers > 0
      ? t('portfolioPage.attention.metric', {
          count: needingAttentionCount,
          total: totalChargers,
        })
      : totalChargers > 0
        ? t('portfolioPage.attention.metric', {
            count: t('portfolioPage.noData'),
            total: totalChargers,
          })
        : t('portfolioPage.noData');

  return {
    title: t('portfolioPage.attention.title'),
    metric,
    description:
      breachOrLateCount == null || totalChargers === 0
        ? t('portfolioPage.attention.noData')
        : t('portfolioPage.attention.description', {
            count: breachOrLateCount,
          }),
    progressPercent:
      needingAttentionCount != null && totalChargers > 0
        ? progressPercent(needingAttentionCount, totalChargers)
        : 0,
  };
}

export function mapComplianceStat(
  compliance: PortfolioComplianceDto,
  t: TFunction<'dashboard'>,
): PortfolioStatCardViewModel {
  const { score, maxScore, belowTarget } = compliance;

  return {
    title: t('portfolioPage.compliance.title'),
    metric:
      score == null
        ? t('portfolioPage.noData')
        : t('portfolioPage.compliance.metric', {
            score,
            max: maxScore,
          }),
    description:
      score == null
        ? t('portfolioPage.compliance.noData')
        : belowTarget == null
          ? t('portfolioPage.compliance.noTarget')
          : belowTarget
            ? t('portfolioPage.compliance.belowTarget')
            : t('portfolioPage.compliance.onTarget'),
    progressPercent: score == null ? 0 : progressPercent(score, maxScore),
  };
}

function mapPenaltyLines(
  penalties: PortfolioPenaltiesDto,
  locale: string,
): PenaltyBreakdownLine[] {
  if (penalties.totalEuros == null) return [];
  return penalties.lines.map((line) => ({
    id: line.id,
    label: line.statusLabel
      ? `${line.siteName} (${line.statusLabel})`
      : line.siteName,
    amount: formatPenaltyEuros(line.amountEuros, locale),
  }));
}

function mapPenaltyCard(
  penalties: PortfolioPenaltiesDto,
  locale: string,
  title: string,
  subtitle: string,
  amountLabel: string,
  noData: string,
): PortfolioPenaltyCardViewModel {
  return {
    title,
    subtitle,
    amountLabel,
    amount:
      penalties.totalEuros == null
        ? noData
        : formatPenaltyEuros(penalties.totalEuros, locale),
    lines: mapPenaltyLines(penalties, locale),
  };
}

export function mapClaimablePenalties(
  kpis: GetPortfolioKpisResponseDto,
  locale: string,
  t: TFunction<'dashboard'>,
): PortfolioPenaltyCardViewModel {
  return mapPenaltyCard(
    kpis.claimablePenalties,
    locale,
    t('portfolioPage.claimable.title'),
    t('portfolioPage.claimable.subtitle'),
    t('portfolioPage.claimable.amountLabel'),
    t('portfolioPage.noData'),
  );
}

export function mapPenaltyExposure(
  kpis: GetPortfolioKpisResponseDto,
  locale: string,
  t: TFunction<'dashboard'>,
): PortfolioPenaltyCardViewModel {
  return mapPenaltyCard(
    kpis.penaltyExposure,
    locale,
    t('portfolioPage.exposure.title'),
    t('portfolioPage.exposure.subtitle'),
    t('portfolioPage.exposure.amountLabel'),
    t('portfolioPage.noData'),
  );
}

export function ownershipLabel(
  ownership: PortfolioOwnership,
  t: TFunction<'dashboard'>,
): string {
  return t(`portfolioPage.ownership.${ownership}`);
}

export function contractStatusLabel(
  status: PortfolioContractStatus,
  t: TFunction<'dashboard'>,
): string {
  return t(`portfolioPage.contractStatus.${status}`);
}

export function mapPropertyRows(
  properties: OperatorPortfolioPropertiesDto,
  t: TFunction<'dashboard'>,
): PortfolioPropertyRowViewModel[] {
  return properties.properties.map((row) => ({
    id: row.id,
    location: row.location,
    chargerCount: row.chargerCount,
    ownership: row.ownership,
    ownershipLabel: ownershipLabel(row.ownership, t),
    contractStatus: row.contractStatus,
    contractStatusLabel: contractStatusLabel(row.contractStatus, t),
    businessImpactTone: row.businessImpactTone,
    businessImpactLabel: row.businessImpactLabel,
    needsAttention: row.needsAttention,
  }));
}

export function filterPropertyRows(
  rows: PortfolioPropertyRowViewModel[],
  search: string,
  attentionOnly: boolean,
): PortfolioPropertyRowViewModel[] {
  const normalized = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (attentionOnly && !row.needsAttention) return false;
    if (!normalized) return true;
    return (
      row.location.toLowerCase().includes(normalized) ||
      row.ownershipLabel.toLowerCase().includes(normalized) ||
      row.contractStatusLabel.toLowerCase().includes(normalized) ||
      row.businessImpactLabel.toLowerCase().includes(normalized)
    );
  });
}

export function paginateRows<T>(
  rows: T[],
  page: number,
  pageSize: number,
): T[] {
  const start = (page - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}
